import { FlexBox } from '@fable/components'
import { css, cx, useTheme } from '@fable/theme'
import { useGoalHabits } from '../../lib/ctx'
import { useStatsTheme } from 'app/webview/pages/stats_profile/theme/ctx'

export const BarChart = () => {
  const [data, maxValue] = useGoalHabits((s) => [s.weekData, s.weekMaxValue])
  const { colors } = useTheme()
  const theme = useStatsTheme((s) => s.theme)

  const colCount = 10
  const nextValues = data.map((_, i) => {
    const nextItem = data[i + 1]
    return nextItem ? nextItem : null
  })

  return (
    <FlexBox
      className={css`
        width: 100%;
        height: 100%;
        position: relative;
      `}
    >
      <div
        className={css`
          width: 100%;
          height: 100%;
          display: grid;
          grid-template-columns: repeat(${colCount}, 1fr);
          gap: 8px;
        `}
      >
        {data.map((item, i) => {
          const goalHeight = (item.number_goals / maxValue) * 100
          const achievedHeight = (item.number_achieved / maxValue) * 100
          const nextGoal = nextValues[i]
          const noGoal = item.number_goals === 0
          const noLine = noGoal || nextGoal?.number_goals === 0

          const getStartY = () => {
            return `${100 - goalHeight}%`
          }

          const getEndY = () => {
            const pos = 100 - ((nextGoal?.number_goals ?? 0) / maxValue) * 100
            return `${pos}%`
          }

          return (
            <FlexBox
              key={i}
              className={css`
                width: 100%;
                height: 100%;
                background-color: ${theme.colors.section.chart.ghost};
                border-radius: 4px;
                position: relative;
                z-index: ${colCount - i};
              `}
              alignItems="flex-end"
            >
              <div
                className={css`
                  display: block;
                  width: 100%;
                  height: ${achievedHeight}%;
                  position: absolute;
                `}
              >
                <span
                  className={cx(
                    'anim__week-bar',
                    css`
                      width: 100%;
                      height: 0%;
                      background-color: ${theme.colors.primary};
                      border-radius: 4px;
                      position: absolute;
                      left: 0;
                      bottom: 0;
                      will-change: height;
                    `
                  )}
                />
              </div>
              <div
                className={cx(
                  'anim__week-line',
                  css`
                    opacity: 0;
                    will-change: opacity;
                  `
                )}
              >
                {i >= data.length - 1 || noLine ? null : (
                  <SvgLine startY={getStartY()} endY={getEndY()} />
                )}
              </div>
            </FlexBox>
          )
        })}
      </div>
      <div
        className={css`
          width: 100%;
          height: 100%;
          position: absolute;
          top: 0;
          left: 0;
          display: grid;
          grid-template-columns: repeat(${colCount}, 1fr);
          gap: 8px;
          z-index: 10;
        `}
      >
        {data.map((item, i) => {
          const goalHeight = (item.number_goals / maxValue) * 100
          const noGoal = item.number_goals === 0

          return (
            <FlexBox
              key={i}
              className={css`
                width: 100%;
                height: 100%;
                position: relative;
              `}
              alignItems="flex-end"
            >
              <div
                className={cx(
                  'anim__week-line',
                  css`
                    opacity: 0;
                    will-change: opacity;
                  `
                )}
              >
                {noGoal ? null : (
                  <span
                    className={css`
                      display: block;
                      width: 10px;
                      height: 10px;
                      background-color: ${theme.colors.section.bg};
                      border: 3px solid ${colors.uiPearl};
                      border-radius: 50%;
                      position: absolute;
                      top: calc(${100 - goalHeight}% - 5px);
                      left: 50%;
                      transform: translateX(-50%);
                    `}
                  />
                )}
              </div>
            </FlexBox>
          )
        })}
      </div>
    </FlexBox>
  )
}

const SvgLine = ({ startY, endY }: { startY: string; endY: string }) => {
  const { colors } = useTheme()

  return (
    <svg
      className={css`
        position: absolute;
        top: 0;
        left: 0;
        width: calc(200% + 10px);
        height: 100%;
        overflow: visible;
      `}
    >
      <line
        x1="21%"
        y1={startY}
        x2="75%"
        y2={endY}
        stroke={colors.uiPearl}
        strokeWidth="3"
      />
    </svg>
  )
}
